import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to handle image URLs
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${BACKEND_URL}${url}`;
};

const AdminNews = () => {
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    author: 'Admin IJSL',
    date: new Date().toISOString().split('T')[0],
    image: null
  });
  const { toast } = useToast();

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const response = await apiService.admin.getNews();
      setNewsList(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('excerpt', formData.excerpt);
      formDataToSend.append('content', formData.content);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('author', formData.author);
      formDataToSend.append('date', formData.date);
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      if (editingNews) {
        await apiService.admin.updateNews(editingNews.id, formDataToSend);
        toast({ title: 'Berhasil', description: 'Tersimpan' });
      } else {
        await apiService.admin.createNews(formDataToSend);
        toast({ title: 'Berhasil', description: 'Tersimpan' });
      }
      
      loadNews();
      closeModal();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menyimpan berita', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus berita ini?')) return;
    try {
      await apiService.admin.deleteNews(id);
      toast({ title: 'Berhasil', description: 'Berita berhasil dihapus' });
      loadNews();
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menghapus berita', variant: 'destructive' });
    }
  };

  const openModal = (news = null) => {
    if (news) {
      setEditingNews(news);
      setFormData({
        title: news.title,
        excerpt: news.excerpt,
        content: news.content,
        category: news.category,
        author: news.author,
        date: new Date(news.date).toISOString().split('T')[0],
        image: null
      });
    } else {
      setEditingNews(null);
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        category: '',
        author: 'Admin IJSL',
        date: new Date().toISOString().split('T')[0],
        image: null
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingNews(null);
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Kelola Berita</h2>
          <button onClick={() => openModal()} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
            <Plus className="h-5 w-5" />
            <span>Tambah Berita</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">Memuat...</div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gambar</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Judul</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {newsList.map((news) => (
                  <tr key={news.id}>
                    <td className="px-6 py-4">
                      <img src={getImageUrl(news.image)} alt={news.title} className="h-12 w-20 object-cover rounded" />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{news.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{news.category}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(news.date).toLocaleDateString('id-ID')}</td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        <button onClick={() => openModal(news)} className="text-blue-600"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(news.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold mb-4">{editingNews ? 'Edit Berita' : 'Tambah Berita'}</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Judul</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Kategori</label>
                  <input type="text" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Ringkasan</label>
                  <textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} className="w-full px-4 py-2 border rounded-md" rows="2" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Konten Lengkap</label>
                  <textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full px-4 py-2 border rounded-md" rows="4" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tanggal</label>
                  <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} className="w-full px-4 py-2 border rounded-md" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Gambar {editingNews && '(kosongkan jika tidak ingin mengubah)'}</label>
                  <input type="file" accept="image/*" onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })} className="w-full px-4 py-2 border rounded-md" required={!editingNews} />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button type="button" onClick={closeModal} className="flex-1 bg-gray-200 px-4 py-2 rounded-md">Batal</button>
                  <button type="submit" className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md">{editingNews ? 'Update' : 'Simpan'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminNews;